// Client-side pre-checks against Meta's actual WhatsApp template guidelines — catches the
// things that get a template rejected, and (worse) the things that get an account's
// templates restricted if repeated: category/content mismatch is the big one, per Meta's
// own enforcement ladder (warning -> rate-limit -> suspend all Utility templates ->
// portfolio-wide restrictions) for a Utility template that's actually promotional.
// Sources (fetched 2026-09):
//   https://developers.facebook.com/documentation/business-messaging/whatsapp/templates/template-categorization
//   https://developers.facebook.com/docs/whatsapp/message-templates/guidelines/
//   https://developers.facebook.com/documentation/business-messaging/whatsapp/templates/authentication-templates/authentication-templates

// Words/phrases that push a template toward "promotional intent" — the exact thing that
// disqualifies it from UTILITY (which must be "non-promotional, not containing any
// promotional or persuasive intent" per Meta's own categorization doc).
const PROMOTIONAL_KEYWORDS = [
  "sale", "discount", "% off", "offer", "deal", "buy now", "shop now",
  "limited time", "free", "win", "giveaway", "exclusive", "promo", "coupon",
  "save big", "save now", "best price", "upgrade now", "subscribe now",
  "renew now", "don't miss", "hurry", "act now", "special price",
];

// Common emoji ranges — good enough for a client-side count-and-warn, not a strict parser.
const EMOJI_REGEX = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{1F1E6}-\u{1F1FF}]/gu;

const OPT_OUT_KEYWORDS = ["stop", "opt out", "opt-out", "unsubscribe"];

export function validateTemplate({ category, headerType, headerText, footerText, buttons, bodyText }) {
  const errors = [];
  const warnings = [];
  const body = bodyText || "";
  const trimmed = body.trim();
  const header = headerText || "";

  // --- Header text: Meta rejects ANY of these regardless of category (confirmed via
  // Meta's own API error: "The message header can't have any new lines, formatting
  // characters, emojis or asterisks") — unlike the body, which allows all of them.
  if (headerType === "TEXT" && header) {
    if (EMOJI_REGEX.test(header)) {
      errors.push("Header text can't contain emojis — Meta rejects this for every category, not just Authentication.");
    }
    if (/\n/.test(header)) {
      errors.push("Header text can't contain line breaks.");
    }
    if (/[*_~]/.test(header)) {
      errors.push("Header text can't use *bold*, _italic_, or ~strikethrough~ formatting — plain text only.");
    }
  }

  // --- Variable placement (Meta rejects all three of these outright) ---
  if (/\{\{\s*\d+\s*\}\}/.test(body)) {
    if (/^\{\{\s*\d+\s*\}\}/.test(trimmed)) {
      errors.push("The body can't start with a variable — add static text before {{1}}.");
    }
    if (/\{\{\s*\d+\s*\}\}$/.test(trimmed)) {
      errors.push("The body can't end with a variable — add static text after the last variable.");
    }
    if (/\{\{\s*\d+\s*\}\}\s*\{\{\s*\d+\s*\}\}/.test(body)) {
      errors.push("Two variables can't sit next to each other — add descriptive text between them.");
    }
    // "Floating" variables — little to no static context around them at all.
    const staticText = trimmed.replace(/\{\{\s*\d+\s*\}\}/g, "").trim();
    if (staticText.length > 0 && staticText.length < 10) {
      warnings.push("The body is mostly just variables with little surrounding context — Meta may reject this as too vague.");
    }
  }

  // --- Whitespace (Meta flags excessive spacing as low-quality formatting) ---
  if (/ {5,}/.test(body)) {
    warnings.push("Body has 5+ consecutive spaces in a row — clean this up before submitting.");
  }

  // --- Category vs. content (the one that risks account-level restrictions, not just
  // this template's rejection, if it repeats) ---
  const lowerBody = body.toLowerCase();
  const promoHit = PROMOTIONAL_KEYWORDS.find((kw) => lowerBody.includes(kw));
  if (category === "UTILITY" && promoHit) {
    warnings.push(
      `Contains "${promoHit}" — reads as promotional. A Utility template with promotional content gets rejected (INCORRECT_CATEGORY) or auto-converted to Marketing later. Repeated mismatches escalate to Meta suspending ALL your Utility templates, then portfolio-wide restrictions — pick Marketing instead if this is genuinely promotional.`
    );
  }

  // --- Authentication — most restrictive category, real OTP structure required ---
  if (category === "AUTHENTICATION") {
    if (headerType !== "NONE") {
      errors.push("Authentication templates can't have a header — Meta only allows a fixed OTP body format for this category.");
    }
    if (EMOJI_REGEX.test(body)) {
      errors.push("Emojis aren't allowed in Authentication templates.");
    }
    if (buttons.length > 0) {
      warnings.push("Authentication templates should use Meta's dedicated one-tap/copy-code OTP button (via Meta's Template Library), not custom buttons — this builder isn't validated for that flow.");
    }
  }

  // --- Marketing: emoji cap + opt-out best practice ---
  if (category === "MARKETING") {
    const emojiCount = (body.match(EMOJI_REGEX) || []).length;
    if (emojiCount > 10) {
      errors.push(`Marketing templates allow at most 10 emojis in the body — this has ${emojiCount}.`);
    }
    const combinedText = `${body} ${footerText || ""}`.toLowerCase();
    if (!OPT_OUT_KEYWORDS.some((kw) => combinedText.includes(kw))) {
      warnings.push(
        "No opt-out language (e.g. \"Reply STOP to opt out\") in the body or footer — not a hard Meta rule, but repeatedly messaging people with no easy way to opt out is exactly what drives up block rate, which is what actually triggers a quality-rating downgrade and messaging-limit throttling."
      );
    }
  }

  // --- Buttons: per-type caps Meta enforces ---
  const urlCount = buttons.filter((b) => b.type === "URL").length;
  const phoneCount = buttons.filter((b) => b.type === "PHONE_NUMBER").length;
  if (urlCount > 2) errors.push("At most 2 \"Visit website\" buttons are allowed.");
  if (phoneCount > 1) errors.push("At most 1 \"Call phone number\" button is allowed.");
  if (buttons.length > 10) errors.push("At most 10 buttons total are allowed.");

  return { errors, warnings };
}

// Meta requires quick-reply and non-quick-reply buttons to not be interleaved — they must
// form two contiguous groups. Reorders on submit so build order in the UI never matters.
export function groupButtonsForSubmit(buttons) {
  const nonQuickReply = buttons.filter((b) => b.type !== "QUICK_REPLY");
  const quickReply = buttons.filter((b) => b.type === "QUICK_REPLY");
  return [...nonQuickReply, ...quickReply];
}
